import 'knockout.validation';
import ko from 'knockout';
import { DateTimeViewModel } from '../date-time-view-model';
import { EmailViewModel } from '../email-view-model';
import {
	initDatePicker,
	initPhone,
	initTimePicker,
	getDefaultEndTime,
} from '../form-helpers';
import { httpClient, HTTPClientError } from '../httpClient';
import {
	CreateMeetingRequest,
	MeetingCreationResult,
	Status,
} from '../interfaces';
import { LocaleUtils } from '../locale-utils';
import { Log } from '../log';
import { PhoneViewModel } from '../phone-view-model';
import { R } from '../Resources.resx';

export class CreateMeetingWithoutNemIdViewModel {
	private _validator: KnockoutValidationGroup = ko.validatedObservable();
	private _isEndDateTimeManuallySet = false;
	private _endDatePicker: any = null;
	private _endTimePicker: any = null;

	public errors = ko.observableArray([]);
	public warning = ko.observable('');
	public success = ko.observable(false);
	public isSubmitting = ko.observable(false);
	public startDateTime = new DateTimeViewModel();
	public endDateTime = new DateTimeViewModel();
	public link = ko.observable<string>();
	public copied = ko.observable<boolean>(false);
	public participantPhoneNumbers = ko.observableArray<PhoneViewModel>([]);
	public participantEmails = ko.observableArray<EmailViewModel>([]);

	public validParticipantPhoneNumbers = ko.pureComputed(() => {
		return this.participantPhoneNumbers().filter((p) => p.isValid());
	});

	public validParticipantEmails = ko.pureComputed(() => {
		return this.participantEmails().filter((p) => p.isValid());
	});

	public hasPhoneOrEmail = ko.pureComputed(() => {
		return (
			this.validParticipantPhoneNumbers().length > 0 ||
			this.validParticipantEmails().length > 0
		);
	});

	public isEndTimeValid = ko.pureComputed(() => {
		const end = this.endDateTime.dateTime();
		const start = this.startDateTime.dateTime();
		return end != null && start != null && end > start;
	});

	public timeZone = LocaleUtils.getCurrentTimeZoneLabel();

	constructor() {
		this._validator = ko.validatedObservable();
		this._validator.errors?.showAllMessages(false);

		this.startDateTime.dateTime.subscribe((start) => {
			if (!start || this._isEndDateTimeManuallySet) return;
			const newEnd = getDefaultEndTime(start);
			this.endDateTime.date(new Date(newEnd));
			this.endDateTime.time(new Date(newEnd));
			this._endDatePicker?.setDate(new Date(newEnd), false);
			this._endTimePicker?.setDate(new Date(newEnd), false);
		});

		this.startDateTime.setCurrentDateAndTime();
	}

	public reset = (): void => {
		this.success(false);
		this.errors([]);
		this.participantPhoneNumbers([new PhoneViewModel()]);
		this.participantEmails([new EmailViewModel()]);
		this._isEndDateTimeManuallySet = false;
		this.initDatepicker();
		this._validator.errors?.showAllMessages(false);
		this.startDateTime.setCurrentDateAndTime();
	};

	private initDatepicker = (): void => {
		initDatePicker('#withoutNemIdStartDate', (selectedDates) => {
			this.startDateTime.date(selectedDates[0]);
		});
		initTimePicker('#withoutNemIdStartTime', (selectedDates) => {
			this.startDateTime.time(selectedDates[0]);
		});
		const defaultEndTime = getDefaultEndTime(new Date());
		this._endDatePicker = initDatePicker(
			'#withoutNemIdEndDate',
			(selectedDates) => {
				this._isEndDateTimeManuallySet = true;
				this.endDateTime.date(selectedDates[0]);
			},
			defaultEndTime,
		);
		this._endTimePicker = initTimePicker(
			'#withoutNemIdEndTime',
			(selectedDates) => {
				this._isEndDateTimeManuallySet = true;
				this.endDateTime.time(selectedDates[0]);
			},
			defaultEndTime,
		);
	};

	public afterRenderPhoneInput = (): void => {
		initPhone('[name=phone]');
	};

	public addPhoneNumberField = () => {
		this.participantPhoneNumbers.push(new PhoneViewModel());
	};

	public addEmailField = () => {
		this.participantEmails.push(new EmailViewModel());
	};

	public createMeeting = (): void => {
		if (!this._validator.isValid()) {
			this._validator.errors?.showAllMessages();
			return;
		}

		if (!this.isEndTimeValid()) {
			return;
		}

		if (this.isSubmitting()) {
			return;
		}

		this.isSubmitting(true);

		const request: CreateMeetingRequest = {
			participantPhoneNumbers: this.validParticipantPhoneNumbers().map((p) =>
				p.value(),
			),
			participantEmails: this.validParticipantEmails().map((p) => p.value()),
			startTime: this.startDateTime.dateTime(),
			endTime: this.endDateTime.dateTime(),
			isPublic: true,
		};

		httpClient
			.post<Status<MeetingCreationResult>>('/api/meetings', request)
			.then((data) => {
				if (data.hasErrors) {
					this.errors(data.errors.map((e) => e.value));
					return;
				}

				const failedRecipients = data.result.failedRecipients;
				const hasFailedRecipients = failedRecipients.length > 0;
				if (hasFailedRecipients) {
					const participantsNotNotified = this.validParticipantEmails().filter(
						(x) => failedRecipients.includes(x.value()),
					);
					if (participantsNotNotified) {
						this.participantEmails.removeAll(participantsNotNotified);
					}

					const htmlListOfUnnotifiedReceipients = failedRecipients.reduce(
						(prev, curr) => {
							return prev + '<li> ' + curr + '</li>';
						},
					);

					this.warning(
						`Invitationen kunne ikke sendes til: <ul> ${htmlListOfUnnotifiedReceipients} </ul>Du kan kopiere linket til mødet og invitere dem via en almindelig e-mail i stedet.`,
					);
				}

				this.link(data.result.meeting.videoRoomLink);
				this.success(true);
			})
			.catch((error) => {
				if (error instanceof HTTPClientError) {
					// The request was made and the server responded with a status code
					// that falls out of the range of 2xx
					Log.error(
						'Error creating meeting. ' +
							error.status +
							': ' +
							(error.data ?? error.message),
					);
				} else {
					// Something happened in setting up the request that triggered an Error
					Log.error('Error creating meeting', error.message);
				}
				this.errors([R.ErrorCreateConsultation]);
			})
			.finally(() => {
				this.isSubmitting(false);
			});
	};

	public copyLink = (): void => {
		const input = document.getElementById(
			'share-link-input',
		) as HTMLInputElement;
		input.select();

		try {
			this.copied(document.execCommand('copy'));
		} catch (ex) {
			this.copied(false);
		}
	};
}
